import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["scoreOptions", "rangeOptions", "selectOptions", "multiselectOptions", "booleanOptions", "entityOptions", "valuesList", "valuesInput", "valueInput", "multiselectValuesList", "multiselectValuesInput", "multiselectValueInput"]

  connect() {
    this.toggleOptions()
    if (this.hasValuesInputTarget) {
      this.initializeValues()
    }
    if (this.hasMultiselectValuesInputTarget) {
      this.initializeMultiselectValues()
    }
  }

  initializeValues() {
    const valuesInput = this.valuesInputTarget
    if (!valuesInput.value) return

    try {
      // Versuche die Werte aus dem versteckten Input zu laden
      const values = JSON.parse(valuesInput.value)
      if (Array.isArray(values)) {
        // Lösche bestehende Werte
        this.valuesListTarget.innerHTML = ''
        // Füge jeden Wert als neues Input-Feld hinzu
        values.forEach(value => this.addValueWithContent(value))
      }
    } catch (e) {
      // Fehler beim Parsen der Werte
    }
  }

  toggleOptions() {
    const fieldTypeSelect = this.element.querySelector('select[name="custom_property_definition[field_type]"]')
    if (!fieldTypeSelect) return

    const fieldType = fieldTypeSelect.value
    
    // Hide all option sections first
    this.hideAllOptions()
    
    // Nur das sichtbare options[values]-Feld aktiv lassen, damit nicht zwei gleiche Namen mitgesendet werden
    if (this.hasValuesInputTarget) {
      this.valuesInputTarget.disabled = (fieldType !== 'select')
    }
    if (this.hasMultiselectValuesInputTarget) {
      this.multiselectValuesInputTarget.disabled = (fieldType !== 'multiselect')
    }
    
    // Show specific options based on field type
    if (fieldType === 'score') {
      this.showScoreOptions()
    } else if (fieldType === 'range') {
      this.showRangeOptions()
    } else if (fieldType === 'select') {
      this.showSelectOptions()
    } else if (fieldType === 'boolean') {
      this.showBooleanOptions()
    } else if (fieldType === 'multiselect') {
      this.showMultiselectOptions()
    } else if (fieldType === 'entity') {
      this.showEntityOptions()
    }
  }

  hideAllOptions() {
    if (this.hasScoreOptionsTarget) {
      this.scoreOptionsTarget.classList.add('hidden')
    }
    if (this.hasRangeOptionsTarget) {
      this.rangeOptionsTarget.classList.add('hidden')
    }
    if (this.hasSelectOptionsTarget) {
      this.selectOptionsTarget.classList.add('hidden')
    }
    if (this.hasBooleanOptionsTarget) {
      this.booleanOptionsTarget.classList.add('hidden')
    }
    if (this.hasMultiselectOptionsTarget) {
      this.multiselectOptionsTarget.classList.add('hidden')
    }
    if (this.hasEntityOptionsTarget) {
      this.entityOptionsTarget.classList.add('hidden')
    }
  }

  showScoreOptions() {
    if (this.hasScoreOptionsTarget) {
      this.scoreOptionsTarget.classList.remove('hidden')
    }
  }
  
  showRangeOptions() {
    if (this.hasRangeOptionsTarget) {
      this.rangeOptionsTarget.classList.remove('hidden')
    }
  }
  
  showSelectOptions() {
    if (this.hasSelectOptionsTarget) {
      this.selectOptionsTarget.classList.remove('hidden')
    }
  }
  
  showBooleanOptions() {
    if (this.hasBooleanOptionsTarget) {
      this.booleanOptionsTarget.classList.remove('hidden')
    }
  }
  
  showMultiselectOptions() {
    if (this.hasMultiselectOptionsTarget) {
      this.multiselectOptionsTarget.classList.remove('hidden')
    }
  }
  
  showEntityOptions() {
    if (this.hasEntityOptionsTarget) {
      this.entityOptionsTarget.classList.remove('hidden')
    }
  }

  addValue(event) {
    event.preventDefault()
    this.addValueWithContent('')
    this.updateValues()
  }

  addValueWithContent(value) {
    const valueItem = document.createElement('div')
    valueItem.className = 'select-value-item flex items-center gap-2 mb-2'
    const escaped = String(value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
    valueItem.innerHTML = `
      <input type="text" name="select_values[]" 
        class="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md" 
        data-custom-property-target="valueInput"
        data-action="change->custom-property#updateValues"
        value="${escaped}">
      <button type="button" class="text-red-600 hover:text-red-800" data-action="click->custom-property#removeValue">
        <i class="fas fa-trash"></i>
      </button>
    `
    this.valuesListTarget.appendChild(valueItem)
  }

  removeValue(event) {
    event.preventDefault()
    const valueItem = event.target.closest('.select-value-item')
    if (valueItem) {
      valueItem.remove()
      this.updateValues()
    }
  }

  updateValues() {
    const values = this.valueInputTargets.map(input => input.value.trim()).filter(v => v.length > 0)
    this.valuesInputTarget.value = JSON.stringify(values)
  }

  // Multiselect-spezifische Methoden
  initializeMultiselectValues() {
    const valuesInput = this.multiselectValuesInputTarget
    if (!valuesInput.value) return

    try {
      const values = JSON.parse(valuesInput.value)
      if (Array.isArray(values)) {
        this.multiselectValuesListTarget.innerHTML = ''
        values.forEach(value => this.addMultiselectValueWithContent(value))
      }
    } catch (e) {
      // Fehler beim Parsen der Werte
    }
  }

  addMultiselectValue(event) {
    event.preventDefault()
    this.addMultiselectValueWithContent('')
    this.updateMultiselectValues()
  }

  addMultiselectValueWithContent(value) {
    const valueItem = document.createElement('div')
    valueItem.className = 'multiselect-value-item flex items-center gap-2 mb-2'
    const escaped = String(value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
    valueItem.innerHTML = `
      <input type="text" name="multiselect_values[]" 
        class="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md" 
        data-custom-property-target="multiselectValueInput"
        data-action="change->custom-property#updateMultiselectValues"
        value="${escaped}">
      <button type="button" class="text-red-600 hover:text-red-800" data-action="click->custom-property#removeMultiselectValue">
        <i class="fas fa-trash"></i>
      </button>
    `
    this.multiselectValuesListTarget.appendChild(valueItem)
  }

  removeMultiselectValue(event) {
    event.preventDefault()
    const valueItem = event.target.closest('.multiselect-value-item')
    if (valueItem) {
      valueItem.remove()
      this.updateMultiselectValues()
    }
  }

  updateMultiselectValues() {
    const values = this.multiselectValueInputTargets.map(input => input.value.trim()).filter(v => v.length > 0)
    this.multiselectValuesInputTarget.value = JSON.stringify(values)
  }
}